import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DollarSign,
  Download,
  Loader2,
  Receipt,
  Search,
  Calendar,
  Filter,
  FileText,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { createReceipt, printReceipt } from '@/lib/invoiceService'
import { formatCurrency, getCurrencyFlag, type SupportedCurrency } from '@/lib/currencyUtils'

interface PaymentRecord {
  id: string
  church_id: string
  church_name: string
  amount: number
  currency: string
  payment_method: string
  transaction_reference: string
  payment_date: string
  status: string
  notes: string | null
  receipt_number: string | null
  recorded_by_name: string
  created_at: string
}

interface PaymentHistoryProps {
  user: AdminUser | null
}

export default function PaymentHistory({ user }: PaymentHistoryProps) {
  const [loading, setLoading] = useState(true)
  const [payments, setPayments] = useState<PaymentRecord[]>([])
  const [filteredPayments, setFilteredPayments] = useState<PaymentRecord[]>([])

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [methodFilter, setMethodFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Stats
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    averagePayment: 0,
    thisMonthTotal: 0,
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              You do not have permission to access payment history.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadPaymentHistory()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [payments, searchTerm, statusFilter, methodFilter, dateFrom, dateTo])

  async function loadPaymentHistory() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('subscription_payment_history')
        .select('*')
        .order('payment_date', { ascending: false })

      if (error) throw error

      setPayments(data || [])
      calculateStats(data || [])
    } catch (error) {
      console.error('Error loading payment history:', error)
    } finally {
      setLoading(false)
    }
  }

  function calculateStats(paymentData: PaymentRecord[]) {
    const total = paymentData.reduce((sum, p) => sum + p.amount, 0)
    const thisMonth = new Date().getMonth()
    const thisYear = new Date().getFullYear()

    const thisMonthPayments = paymentData.filter(p => {
      const date = new Date(p.payment_date)
      return date.getMonth() === thisMonth && date.getFullYear() === thisYear
    })

    const thisMonthTotal = thisMonthPayments.reduce((sum, p) => sum + p.amount, 0)

    setStats({
      totalPayments: paymentData.length,
      totalAmount: total,
      averagePayment: paymentData.length > 0 ? total / paymentData.length : 0,
      thisMonthTotal,
    })
  }

  function applyFilters() {
    let filtered = [...payments]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.church_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.transaction_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter)
    }

    // Payment method filter
    if (methodFilter !== 'all') {
      filtered = filtered.filter(p => p.payment_method === methodFilter)
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(p => new Date(p.payment_date) >= new Date(dateFrom))
    }
    if (dateTo) {
      filtered = filtered.filter(p => new Date(p.payment_date) <= new Date(dateTo))
    }

    setFilteredPayments(filtered)
  }

  function exportToCSV() {
    const headers = [
      'Date',
      'Church',
      'Amount',
      'Currency',
      'Payment Method',
      'Transaction Reference',
      'Status',
      'Recorded By',
      'Notes'
    ]

    const rows = filteredPayments.map(p => [
      new Date(p.payment_date).toLocaleDateString(),
      p.church_name,
      p.amount.toFixed(2),
      p.currency,
      p.payment_method,
      p.transaction_reference || '',
      p.status,
      p.recorded_by_name,
      p.notes || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payment-history-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function handlePrintReceipt(payment: PaymentRecord) {
    try {
      const receipt = await createReceipt(
        payment.church_id,
        payment.payment_method,
        payment.transaction_reference,
        payment.payment_date
      )

      if (receipt) {
        printReceipt(receipt)
      }
    } catch (error) {
      console.error('Error printing receipt:', error)
      alert('Failed to print receipt')
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>
      case 'failed':
        return <Badge className="bg-red-500">Failed</Badge>
      case 'refunded':
        return <Badge className="bg-gray-500">Refunded</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  function getMethodBadge(method: string) {
    const badges: Record<string, string> = {
      check: 'bg-blue-500',
      bank_transfer: 'bg-sky-500',
      cash: 'bg-green-500',
      mobile_money: 'bg-orange-500',
      stripe: 'bg-indigo-500',
      paypal: 'bg-blue-600',
    }

    return (
      <Badge className={badges[method] || 'bg-gray-500'}>
        {method.replace('_', ' ').toUpperCase()}
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading payment history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Payment History</h1>
          <p className="text-muted-foreground">View and manage all subscription payments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Payments</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPayments}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">GHS {stats.totalAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Payment</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">GHS {stats.averagePayment.toFixed(0)}</div>
            <p className="text-xs text-muted-foreground">Per transaction</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">GHS {stats.thisMonthTotal.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{new Date().toLocaleString('default', { month: 'long' })}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search church, reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>

            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Payment Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="mobile_money">Mobile Money</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />

            <Input
              type="date"
              placeholder="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Records ({filteredPayments.length})</CardTitle>
          <CardDescription>
            {filteredPayments.length === payments.length
              ? 'Showing all payments'
              : `Filtered ${filteredPayments.length} of ${payments.length} payments`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Church</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recorded By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No payments found
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <div className="font-medium">
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(payment.created_at).toLocaleTimeString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{payment.church_name}</div>
                      {payment.receipt_number && (
                        <div className="text-xs text-muted-foreground">
                          {payment.receipt_number}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-bold text-lg">
                        {getCurrencyFlag(payment.currency as SupportedCurrency)} {formatCurrency(payment.amount, payment.currency as SupportedCurrency)}
                      </div>
                    </TableCell>
                    <TableCell>{getMethodBadge(payment.payment_method)}</TableCell>
                    <TableCell>
                      <div className="text-sm font-mono">
                        {payment.transaction_reference || '-'}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(payment.status)}</TableCell>
                    <TableCell>
                      <div className="text-sm">{payment.recorded_by_name}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrintReceipt(payment)}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Receipt
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
